import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Shared, batched like counts for reviews.
 *
 * Every ReviewCard asks for its own review id, but requests made in the same
 * tick are coalesced into a single `user_likes` query (chunked at 100 ids to
 * respect the platform `.in()` limit). Results are cached per review id so
 * navigating away and back renders the known count immediately.
 */

export const reviewLikeCountKey = (reviewId: string) => ['review-like-count', reviewId] as const;

const PAGE_SIZE = 1000;

let batchIds = new Set<string>();
let batchPromise: Promise<Map<string, number>> | null = null;

const runBatch = (): Promise<Map<string, number>> =>
  new Promise((resolve, reject) => {
    setTimeout(async () => {
      const ids = Array.from(batchIds);
      batchIds = new Set();
      batchPromise = null;
      try {
        const counts = new Map<string, number>();
        ids.forEach((id) => counts.set(id, 0));
        for (let i = 0; i < ids.length; i += 100) {
          const chunk = ids.slice(i, i + 100);
          // Page through the rows: a single response can be capped by the
          // server row limit, which would silently undercount popular reviews.
          let from = 0;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { data, error } = await supabase
              .from('user_likes')
              .select('review_id')
              .eq('item_type', 'review')
              .in('review_id', chunk)
              .range(from, from + PAGE_SIZE - 1);
            if (error) throw error;
            const rows = data || [];
            rows.forEach((row: { review_id: string | null }) => {
              if (!row.review_id) return;
              counts.set(row.review_id, (counts.get(row.review_id) || 0) + 1);
            });
            if (rows.length < PAGE_SIZE) break;
            from += PAGE_SIZE;
          }
        }
        resolve(counts);
      } catch (err) {
        reject(err);
      }
    }, 0);
  });

const loadReviewLikeCount = (reviewId: string): Promise<number> => {
  batchIds.add(reviewId);
  if (!batchPromise) batchPromise = runBatch();
  return batchPromise.then((counts) => counts.get(reviewId) ?? 0);
};

export const useReviewLikeCount = (reviewId: string) => {
  const { data } = useQuery({
    queryKey: reviewLikeCountKey(reviewId),
    queryFn: () => loadReviewLikeCount(reviewId),
    enabled: !!reviewId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return { count: data, resolved: data !== undefined };
};
